import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Block,
  CheckCircle,
  Email,
  Refresh,
} from '@mui/icons-material';
import api from '../../utils/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

interface SenderEntry {
  id: string;
  senderEmail: string;
  senderDomain?: string;
  listType: 'whitelist' | 'blacklist';
  reason?: string;
  createdAt: string;
}

const AntiSpamSettings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [blacklist, setBlacklist] = useState<SenderEntry[]>([]);
  const [whitelist, setWhitelist] = useState<SenderEntry[]>([]);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addType, setAddType] = useState<'blacklist' | 'whitelist'>('blacklist');
  const [newEmail, setNewEmail] = useState('');

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const fetchLists = useCallback(async () => {
    setLoading(true);
    try {
      const [blRes, wlRes] = await Promise.all([
        api.get('/emails/sender-list', { params: { type: 'blacklist' } }),
        api.get('/emails/sender-list', { params: { type: 'whitelist' } }),
      ]);
      setBlacklist(Array.isArray(blRes.data) ? blRes.data : blRes.data?.data || []);
      setWhitelist(Array.isArray(wlRes.data) ? wlRes.data : wlRes.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch sender lists:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  const handleAdd = async () => {
    const email = newEmail.trim();
    if (!email) return;
    setLoading(true);
    try {
      await api.post('/emails/sender-list', { email, type: addType });
      await fetchLists();
      setSnackbar({ open: true, message: `Added to ${addType === 'blacklist' ? 'block' : 'allow'} list`, severity: 'success' });
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Failed to add sender';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setLoading(false);
      setNewEmail('');
      setAddDialogOpen(false);
    }
  };

  const handleRemove = async (entryId: string, listLabel: string) => {
    setLoading(true);
    try {
      await api.delete(`/emails/sender-list/${entryId}`);
      await fetchLists();
      setSnackbar({ open: true, message: `Removed from ${listLabel}`, severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to remove sender', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const renderList = (entries: SenderEntry[], listLabel: string) => {
    if (entries.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          No senders in {listLabel}
        </Box>
      );
    }
    return (
      <List dense>
        {entries.map((entry) => (
          <ListItem
            key={entry.id}
            secondaryAction={
              <IconButton
                edge="end"
                onClick={() => handleRemove(entry.id, listLabel)}
                disabled={loading}
              >
                <DeleteIcon />
              </IconButton>
            }
          >
            <ListItemText
              primary={entry.senderEmail}
              secondary={
                [
                  entry.reason,
                  `Added ${new Date(entry.createdAt).toLocaleDateString()}`,
                ].filter(Boolean).join(' · ')
              }
            />
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '8px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.primary' }}>
            Blocked & Trusted Senders
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your personal blocked and trusted sender lists. Emails from blocked senders go to Junk.
          </Typography>
        </Box>
        <Button startIcon={<Refresh />} onClick={fetchLists} disabled={loading}>
          Refresh
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, mt: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Block fontSize="small" />
                Blocked Senders ({blacklist.length})
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle fontSize="small" />
                Trusted Senders ({whitelist.length})
              </Box>
            }
          />
        </Tabs>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Blocked Senders Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Emails from blocked senders are automatically moved to your Junk folder. You can also block senders by clicking "Report Spam" on any email.
          </Typography>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            color="error"
            size="small"
            sx={{ ml: 2, whiteSpace: 'nowrap' }}
            onClick={() => { setAddType('blacklist'); setAddDialogOpen(true); }}
          >
            Block Sender
          </Button>
        </Box>
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          {renderList(blacklist, 'block list')}
        </Paper>
      </TabPanel>

      {/* Trusted Senders Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Emails from trusted senders will always be delivered to your Inbox, even if detected as spam. You can also trust senders by clicking "Not Spam" on any email.
          </Typography>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            color="success"
            size="small"
            sx={{ ml: 2, whiteSpace: 'nowrap' }}
            onClick={() => { setAddType('whitelist'); setAddDialogOpen(true); }}
          >
            Trust Sender
          </Button>
        </Box>
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          {renderList(whitelist, 'trusted list')}
        </Paper>
      </TabPanel>

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {addType === 'blacklist' ? 'Block a Sender' : 'Trust a Sender'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Email Address"
              placeholder="example@domain.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              InputProps={{ startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} /> }}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddDialogOpen(false); setNewEmail(''); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            color={addType === 'blacklist' ? 'error' : 'success'}
            disabled={loading || !newEmail.trim()}
          >
            {addType === 'blacklist' ? 'Block' : 'Trust'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AntiSpamSettings;
